/**
 * BackButton — botón "Volver" que prioriza el historial del browser
 * para preservar search params del referrer (filtros del catálogo,
 * estado de la lista, etc.).
 *
 * Comportamiento:
 *   1. Si hay historial dentro de la app (`window.history.length > 1`),
 *      ejecuta `router.history.back()`. Esto restaura la URL EXACTA
 *      previa, incluyendo cualquier `?q=…&tab=…&type=…`.
 *   2. Si no hay historial (deep link, recarga directa en la página de
 *      detalle), llama al callback `fallback()` que el caller provee —
 *      ahí se hace el `navigate({ to, params })` tipado para llegar al
 *      listado raíz.
 *
 * Pattern estándar (Linear/Notion/Jira): "back to inbox" siempre va al
 * estado donde el usuario estaba, no a una vista fresca. Sin esto, los
 * filtros del listado se pierden al entrar a un detalle y los usuarios
 * tienen que volver a aplicarlos.
 */

import type { ReactNode } from 'react'
import { useRouter } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

interface BackButtonProps {
  /** Fallback cuando no hay historial — el caller hace `navigate({ to,
   *  params })` con tipos del router. */
  fallback: () => void
  children: ReactNode
  className?: string
}

export function BackButton({
  fallback,
  children,
  className,
}: BackButtonProps) {
  const router = useRouter()
  const baseClass =
    'inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink'
  return (
    <button
      type="button"
      onClick={() => {
        if (window.history.length > 1) {
          router.history.back()
        } else {
          fallback()
        }
      }}
      className={className ?? baseClass}
    >
      <ArrowLeft className="w-3.5 h-3.5" />
      {children}
    </button>
  )
}
